import { Link } from "react-router-dom";
import { AlertCircle, AlertTriangle, Info, ExternalLink, RefreshCw } from "lucide-react";
import { ApiError } from "../../lib/api";
import { Button } from "./Button";

export type StatusVariant = "error" | "warning" | "info";

export interface StatusAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

export interface StatusMessageProps {
  variant?: StatusVariant;
  title: string;
  message: string;
  action?: StatusAction;
  requestId?: string;
  compact?: boolean;
  className?: string;
  supportLink?: string;
}

const SUPPORT_URL = "https://t.me/Soorgaa_bot";

export function StatusMessage({
  variant = "info",
  title,
  message,
  action,
  requestId,
  compact = false,
  className = "",
  supportLink = SUPPORT_URL,
}: StatusMessageProps) {
  const iconMap = {
    error: <AlertCircle className="text-destructive shrink-0" size={compact ? 16 : 22} />,
    warning: <AlertTriangle className="text-warning shrink-0" size={compact ? 16 : 22} />,
    info: <Info className="text-primary shrink-0" size={compact ? 16 : 22} />,
  };

  if (compact) {
    return (
      <div
        className={`flex items-center justify-between gap-3 p-3 rounded-md border border-border bg-card text-xs ${className}`}
        role="alert"
      >
        <div className="flex items-center gap-2 min-w-0">
          {iconMap[variant]}
          <div className="truncate">
            <span className="font-semibold text-foreground mr-1.5">{title}:</span>
            <span className="text-muted-foreground">{message}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {action && (
            action.href ? (
              <Link to={action.href} className="font-semibold text-primary hover:underline">
                {action.label}
              </Link>
            ) : (
              <button
                type="button"
                onClick={action.onClick}
                className="font-semibold text-primary hover:underline inline-flex items-center gap-1 cursor-pointer"
              >
                <RefreshCw size={11} /> {action.label}
              </button>
            )
          )}
          <a
            href={supportLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            Support <ExternalLink size={10} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-card text-foreground border border-border rounded-lg p-6 space-y-4 text-center max-w-md mx-auto shadow-xs ${className}`}
      role="alert"
    >
      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto">
        {iconMap[variant]}
      </div>

      <div className="space-y-1.5">
        <h3 className="font-bold text-base text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{message}</p>
        {requestId && (
          <p className="text-xs font-mono text-muted-foreground/80 pt-1">
            Ref: {requestId}
          </p>
        )}
      </div>

      <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
        {action && (
          action.href ? (
            <Button
              to={action.href}
              variant="primary"
              size="sm"
              className="w-full sm:w-auto"
            >
              {action.label}
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={action.onClick}
              icon={<RefreshCw size={13} />}
              className="w-full sm:w-auto"
            >
              {action.label}
            </Button>
          )
        )}

        <a
          href={supportLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1 py-1"
        >
          <span>Contact support</span>
          <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}

export interface MappedApiError {
  title: string;
  message: string;
  variant: StatusVariant;
  isSessionExpired?: boolean;
  isPremiumRequired?: boolean;
  isNotFound?: boolean;
  requestId?: string;
}

/**
 * Maps raw API errors to plain, calm messages adhering to student copy rules:
 * - 401 session ended -> "Please sign in again."
 * - 403 premium_required -> locked UI / "Upgrade to premium to unlock this content."
 * - 404 -> not found
 * - 429 -> "Too many attempts. Please try again in a minute."
 * - 5xx / network -> "Something went wrong. Please try again." + "Ref: {request id}"
 */
export function mapApiError(err: unknown): MappedApiError {
  let status = 0;
  let code: string | undefined;
  let reqId: string | undefined;
  let rawMsg = "";

  if (err instanceof ApiError) {
    status = err.status;
    code = err.code;
    reqId = err.requestId;
    rawMsg = err.message;
  } else if (err && typeof err === "object") {
    const e = err as Record<string, unknown>;
    if (typeof e.status === "number") status = e.status;
    if (typeof e.code === "string") code = e.code;
    if (typeof e.requestId === "string") reqId = e.requestId;
    if (typeof e.message === "string") rawMsg = e.message;
  } else if (typeof err === "string") {
    rawMsg = err;
  }

  // 401 session ended — log out and navigate to landing page directly
  if (status === 401 || code === "session_expired" || /session expired/i.test(rawMsg)) {
    if (typeof window !== "undefined" && window.location.pathname !== "/" && !window.location.pathname.startsWith("/auth")) {
      try {
        localStorage.removeItem("soorgaa_auth_tokens");
      } catch { /* ignore */ }
      window.location.href = "/";
    }
    return {
      title: "Session ended",
      message: "Please sign in again.",
      variant: "info",
      isSessionExpired: true,
      requestId: reqId,
    };
  }

  // 403 premium_required
  if (status === 403 && (code === "premium_required" || /premium/i.test(rawMsg))) {
    return {
      title: "Premium required",
      message: "Upgrade to premium to unlock this content.",
      variant: "warning",
      isPremiumRequired: true,
      requestId: reqId,
    };
  }

  // 404 not found
  if (status === 404 || /not found/i.test(rawMsg)) {
    return {
      title: "Not found",
      message: "The requested item could not be found.",
      variant: "warning",
      isNotFound: true,
      requestId: reqId,
    };
  }

  // 429 rate limit
  if (status === 429) {
    return {
      title: "Please wait a moment",
      message: "There have been several attempts. Please try again in a minute.",
      variant: "warning",
      requestId: reqId,
    };
  }

  // 5xx or network
  const refSuffix = reqId ? ` Ref: ${reqId}` : "";
  return {
    title: "We could not complete that",
    message: `We could not complete your request. Please try again.${refSuffix}`,
    variant: "error",
    requestId: reqId,
  };
}
